import { useState } from "react";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import moment from "moment/moment";
// @ts-expect-error pure js
import useProcesses from "../../hooks/use-processes";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import ButtonCard from "../UI/ButtonCard";
import Card from "../UI/Card";
import Text from "../UI/Text";
import Title from "../UI/Title";
import ModalDialog, { ModalDialogBody } from "../UI/ModalDialog";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import { ProcessDepartIcon } from "../UI/Icons";
// @ts-expect-error pure js
import { ProcessType } from "../../utils/constants/processes";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import ProcessChoice from "./ProcessChoice";
import ProcessDepart from "./Process/ProcessDepart";
import type { Process } from "../../models/process";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import styles from "./Processes.module.css";

const PROCESS_LABELS: Record<string, string> = {
  [ProcessType.DEPART]: "Départ initié par le locataire",
  [ProcessType.DEPART_BAILLEUR]: "Départ initié par le bailleur",
};

type ProcessId = Process["id"];

interface ProcessesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Appelé quand un processus est terminé (le locataire est archivé). */
  onFinish: () => void;
  /** Locataire présélectionné dans le formulaire de création. */
  tenantId?: number | string;
  /** Processus ouvert directement, sans passer par la liste. */
  initialProcessId?: ProcessId | null;
}

/**
 * Modale des processus de fin de bail : liste des processus en cours et
 * création, puis le détail d'un processus avec ses étapes.
 */
export default function Processes({
  open,
  onOpenChange,
  onFinish,
  tenantId = "",
  initialProcessId = null,
}: ProcessesProps) {
  const { activeProcesses, createProcess, updateProcess, deleteProcess } =
    useProcesses() as {
      activeProcesses: Process[];
      createProcess: (values: {
        id_tenant: number | string;
        type: string;
        is_active: boolean;
        entries: Process["entries"];
      }) => Promise<Process>;
      updateProcess: (id: ProcessId, values: Partial<Process>) => Promise<void>;
      deleteProcess: (id: ProcessId) => Promise<void>;
    };
  const { tenants } = useTenants() as { tenants: Tenant[] };
  const toast = useToast();
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property | undefined>;
  };
  // `undefined` : suit `initialProcessId` ; `null` : la liste.
  const [selection, setSelection] = useState<ProcessId | null | undefined>(
    undefined,
  );
  const selectedId = selection === undefined ? initialProcessId : selection;
  const selectedProcess =
    selectedId != null
      ? activeProcesses.find((process) => process.id === selectedId)
      : undefined;
  const selectedTenant = selectedProcess
    ? tenants.find((tenant) => tenant.id === selectedProcess.id_tenant)
    : undefined;

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setSelection(undefined);
    onOpenChange(nextOpen);
  }

  async function submitHandler(
    values: { id_tenant: number | string; type: string | null },
    { resetForm }: { resetForm: () => void },
  ) {
    try {
      const response = await createProcess({
        is_active: true,
        entries: {},
        id_tenant: values.id_tenant,
        type: values.type ?? "",
      });
      setSelection(response.id);
      resetForm();
    } catch (error) {
      toast.error({
        title: "Impossible de créer le processus",
        description: handleAPIError(error),
      });
    }
  }

  async function finishProcessHandler(processId: ProcessId) {
    await updateProcess(processId, { is_active: false });
    setSelection(undefined);
    onFinish();
  }

  async function deleteProcessHandler(processId: ProcessId) {
    await deleteProcess(processId);
    setSelection(null);
  }

  const subtitle = selectedTenant
    ? `${selectedTenant.nom} · ${
        selectedTenant.fin
          ? `Départ prévu le ${moment(selectedTenant.fin).format("DD/MM/YYYY")}`
          : "Date de départ à définir"
      }`
    : undefined;

  return (
    <ModalDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="md"
      header={
        selectedProcess && selectedTenant
          ? "Processus de fin de bail"
          : "Processus"
      }
      subtitle={subtitle}
    >
      {selectedProcess && selectedTenant ? (
        <ProcessDepart
          process={selectedProcess}
          tenant={selectedTenant}
          profileId={
            propertiesById[selectedTenant.id_bien]?.id_profile_nom_owner
          }
          onFinish={finishProcessHandler}
          onDelete={deleteProcessHandler}
          onBack={() => setSelection(null)}
          onClose={() => handleOpenChange(false)}
        />
      ) : (
        <ModalDialogBody className={styles.list}>
          <img src="/Processus.png" alt="" className={styles.illustration} />
          <Title variant="kilo" weight="bold">
            Nouveau processus
          </Title>
          <Formik
            initialValues={{ id_tenant: tenantId, type: null }}
            enableReinitialize={true}
            validationSchema={object({
              id_tenant: string().required(" "),
              type: string().required(" "),
            })}
            onSubmit={submitHandler}
          >
            <Form className={styles.choices}>
              <ProcessChoice
                processType={ProcessType.DEPART}
                text={PROCESS_LABELS[ProcessType.DEPART]}
                icon={<ProcessDepartIcon />}
              />
              <ProcessChoice
                processType={ProcessType.DEPART_BAILLEUR}
                text={PROCESS_LABELS[ProcessType.DEPART_BAILLEUR]}
                icon={<ProcessDepartIcon />}
              />
            </Form>
          </Formik>
          <Title variant="kilo" weight="bold">
            Processus en cours
          </Title>
          {activeProcesses.length > 0 ? (
            <div className={styles.processList}>
              {activeProcesses.map((process) => {
                const tenant = tenants.find((t) => t.id === process.id_tenant);
                if (!tenant) return null;
                return (
                  <ButtonCard
                    onClick={() => setSelection(process.id)}
                    key={process.id}
                    className={styles.processCard}
                  >
                    <Text weight="bold">{tenant.nom}</Text>
                    <Text>{PROCESS_LABELS[process.type] ?? process.type}</Text>
                  </ButtonCard>
                );
              })}
            </div>
          ) : (
            <Card className={styles.empty}>
              <Text>Aucun processus en cours</Text>
            </Card>
          )}
        </ModalDialogBody>
      )}
    </ModalDialog>
  );
}
